import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
// ✅ CHANGED: Removed IServerSideGetRowsParams (wrong for infinite model)
//             Added IDatasource + IGetRowsParams (correct for rowModelType: "infinite")
import { ColDef, IDatasource, IGetRowsParams } from 'ag-grid-community';
import { ServerRequest } from 'src/app/_models/global.data.model';
import { ErrorUtil } from 'src/app/_utilities/error';
import { SubscriptionUtil } from 'src/app/_utilities/subscription';
import { TableHeaderToolTipComponent } from '../../table-header-tool-tip/table-header-tool-tip.component';
import { UsersService } from 'src/app/_services/admin/users.service';
import { LanguageTranslateService } from 'src/app/_services/language-translate.service';
import { Global } from 'src/app/_utilities/global';

var translateData: any;

@Component({
  selector: 'app-custella-users',
  templateUrl: './custella-users.component.html',
  styleUrls: ['./custella-users.component.scss'],
  standalone: false,
})
export class CustellaUsersComponent extends SubscriptionUtil implements OnInit, OnDestroy {

  @Output() itemSelector = new EventEmitter();
  @Output() closeView = new EventEmitter();

  gridApi: any;
  gridColumnApi: any;
  columnDefs: any = [];
  defaultColDef: any;
  frameworkComponents: any;
  gridParams: any;
  startFrom: any;
  product: any;
  pageSize: any = 25;
  parameters: any;
  gridOptions: any;
  overlayNoRowsTemplate: string;
  productsFamilies: any;
  productsType: any;
  currentRecordNumber: any;
  totalRecord: any;
  searchValue: any = null;
  checklistData: any;
  translate: any;

  // ✅ ADDED: headerIdMap — maps field names to their accessibility header element IDs
  private readonly headerIdMap: any = {
    'fullName': 'Tbl_HeadSetUserNo',
  };

  constructor(
    private usersService: UsersService,
    private errorUtil: ErrorUtil,
    private languageTranslateService: LanguageTranslateService,
  ) {
    super();

    this.gridOptions = {
      cacheBlockSize: 25,
      rowModelType: "infinite",
      sortingOrder: ["desc", "asc"],
      onGridSizeChanged: (params: any) => {
        params.api.sizeColumnsToFit();
      },
      tooltipShowDelay: 100,
    };

    this.defaultColDef = {
      enableRowGroup: true,
      enablePivot: true,
      enableValue: true,
      sortable: true,
      resizable: true,
      filter: false,
      tooltipComponent: TableHeaderToolTipComponent,
    };

    // ✅ NOTE: columnDefs intentionally left empty here.
    //    Original code built columnDefs in the constructor using this.translate,
    //    but getLanguageData() is async — this.translate is always undefined at
    //    construction time. Columns are now built in initializeColumns() which
    //    is called from getLanguageData() once translate data is available.
  }

  ngOnInit(): void {
    // ✅ MOVED: getLanguageData() called in ngOnInit (not constructor)
    //    so Angular DI is fully resolved before the subscription is set up
    this.getLanguageData();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  // ✅ ADDED: called from (firstDataRendered) in template — sets accessibility header IDs
  public onFirstDataRendered(params: any) {
    Global.setHeaderIds(params, this.headerIdMap);
  }

  /********************************** Cell Click **************************/
  public viewUser($event: any) {
    if ($event.colDef.field === "fullName") {
      this.selectItem($event.data);
    }
  }

  selectItem(val: any) {
    this.itemSelector.emit(val);
  }

  public closeTable() {
    this.closeView.emit(false);
  }

  push(obs: any) {
    super.push(obs);
  }

  /********************************** Search **************************/
  quickSearch() {
    this.onGridReady(this.gridParams);
  }

  /********************************** On Grid Ready **************************/
  onGridReady(params: any) {
    this.gridParams = params;
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit();

    // ✅ CHANGED: IDatasource with IGetRowsParams (infinite row model)
    const dataSource: IDatasource = {
      getRows: (params: IGetRowsParams) => {
        this.getServerSideData(params);
      },
    };

    // ✅ UNCHANGED: setGridOption("datasource") was already used here — keep as-is
    this.gridApi.setGridOption("datasource", dataSource);
  }

  /********************************** Get Server Side Data **************************/
  // ✅ CHANGED: IServerSideGetRowsParams → IGetRowsParams
  getServerSideData(agGridGetRowsParams: IGetRowsParams) {
    this.gridApi.hideOverlay();
    const serverRequest = this.getRequestParams(agGridGetRowsParams);

    this.push(
      this.usersService.getAllUserForMachinesSpecialist(this.searchValue, serverRequest).subscribe(
        (data: any) => {
          this.checklistData = data.body.data;
          this.totalRecord = data.body.total;
          this.setGridData(agGridGetRowsParams, this.checklistData, data.body.total, data.body.status);
        },
        (err) => {
          this.errorUtil.setErrorMessage(err);
        }
      )
    );
  }

  /********************************** Set Grid Data **************************/
  // ✅ CHANGED: IServerSideGetRowsParams → IGetRowsParams
  // ✅ CHANGED: .success({rowData, rowCount}) → successCallback(data, totalCount)
  private setGridData(
    agGridGetRowsParams: IGetRowsParams,
    resultItems: any[],
    totalCount: number,
    status: string
  ) {
    if (this.checklistData.length === 0) {
      this.gridApi.showNoRowsOverlay();
    } else {
      // ✅ CHANGED: successCallback(data, total) replaces .success({rowData, rowCount})
      agGridGetRowsParams.successCallback(this.checklistData, totalCount);
    }
  }

  /********************************** Request Params **************************/
  getRequestParams(agGridRequest: any): ServerRequest {
    if (agGridRequest.sortModel.length <= 0) {
      this.parameters = { colId: "superVisor", sort: "desc" };
      agGridRequest.sortModel.push(this.parameters);
    }

    this.currentRecordNumber = agGridRequest.startRow;
    return {
      startRow: agGridRequest.startRow,
      pageSize: 25,
      filterModel: null,
      sortModel: agGridRequest.sortModel,
    };
  }

  /********************************** Language + Columns **************************/
  getLanguageData() {
    this.push(
      this.languageTranslateService.wordSrc$.subscribe((data) => {
        if (Object.keys(data).length != 0) {
          this.translate = data;
          translateData = data;
          // ✅ MOVED: build columns here after translate is available
          this.initializeColumns();
        }
      })
    );
  }

  /********************************** Initialize Columns **************************/
  // ✅ ADDED: extracted from constructor — translate data is guaranteed to exist here
  initializeColumns() {
    this.columnDefs = [
      {
        headerName: this.translate?.setup.users.users.fields.fullName.singular,
        field: "fullName",
        width: 150,
        sortable: true,
        cellClass: function (params: any) { return ["table_default_color"]; },
        headerTooltip: "#",
        // ✅ CHANGED: removed headerComponentParams.template — ID tracked via headerIdMap + onFirstDataRendered
        cellRenderer: function (params: any) {
          if (params.data) {
            return '<p id="' + params.data.rowId + '">' + params.data.fullName + '</p>';
          }
        },
      },
      {
        headerName: this.translate?.setup.users.users.fields.username.singular,
        field: "login",
        width: 150,
        sortable: true,
        headerTooltip: "#",
      },
      {
        headerName: this.translate?.setup.users.users.fields.email.singular,
        field: "email",
        width: 150,
        sortable: true,
        headerTooltip: "#",
      },
      {
        headerName: this.translate?.setup.users.users.fields.manager.singular,
        field: "superVisor.fullName",
        width: 80,
        sortable: true,
        headerTooltip: "#",
      },
      {
        headerName: this.translate?.setup.users.users.fields.access.singular,
        field: "profile.name",
        width: 80,
        sortable: true,
        headerTooltip: "#",
      },
      {
        headerName: this.translate?.setup.users.users.fields.hierarchy.singular,
        field: "role.name",
        width: 100,
        sortable: true,
        headerTooltip: "#",
      },
      {
        headerName: this.translate?.setup.users.users.fields.status.singular,
        field: 'status',
        width: 100,
        headerTooltip: "#",
        cellRenderer: function (params: any) {
          if (params.data) {
            if (params.data.status == 'ACTIVE') {
              return '<span class="p-mr-2  p-tag p-component p-tag-success" ><span class="p-tag-value">' + translateData.general.status.active.singular + '</span></span>';
            } else if (params.data.status == 'DEACTIVE') {
              return '<span class="p-mr-2  p-tag p-component p-tag-Deactivate" ><span class="p-tag-value text-dark">' + translateData.general.status.deactivated.singular + '</span></span>';
            } else if (params.data.status == 'PENDING_ACTIVATION') {
              return '<span class="p-mr-2  p-tag p-component p-tag-Pending-Activation" ><span class="p-tag-value text-dark">' + translateData.general.status.pendingActivation.singular + '</span></span>';
            } else if (params.data.status == 'INACTIVE') {
              return '<span class="p-mr-2  p-tag p-component p-tag-danger" ><span class="p-tag-value text-dark">' + translateData.general.status.inactive.singular + '</span></span>';
            } else {
              return '<span class="p-mr-2  p-tag p-component  p-tag-Suspended" ><span class="p-tag-value text-dark">' + translateData.general.status.suspended.singular + '</span></span>';
            }
          }
        },
      },
    ];

    // ✅ Update grid if already initialised (language reload scenario)
    if (this.gridApi) {
      this.gridApi.setGridOption("columnDefs", this.columnDefs);
      setTimeout(() => { this.gridApi.sizeColumnsToFit(); }, 500);
    }
  }
}